// Pan + zoom for the seat map (Biletix-style LOD).
//
// • At scale=1 the content fills the container exactly (cannot zoom out further).
// • `zoomToBox(box)` smoothly animates to fit a sub-region — used by overview
//   blocks to "zoom into a section" on click.
// • `transform.scale` is exposed via the children render prop so callers can
//   crossfade overview blocks ↔ seat detail based on zoom level.

import {
  type PointerEvent,
  type WheelEvent,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

export type Transform = { x: number; y: number; scale: number };
export type Bounds = { x: number; y: number; w: number; h: number };

export type PanZoomHandle = {
  zoomToBox: (box: Bounds, padding?: number) => void;
  reset: () => void;
  getScale: () => number;
};

type Props = {
  viewBox: Bounds;
  children: (transform: Transform) => React.ReactNode;
  className?: string;
  maxScale?: number;
};

const ANIM_MS = 380;
const easeOutCubic = (t: number) => 1 - (1 - t) ** 3;

export const PanZoom = forwardRef<PanZoomHandle, Props>(function PanZoom(
  { viewBox: contentBox, children, className, maxScale = 5 },
  ref,
) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const svgRef = useRef<SVGSVGElement | null>(null);
  const [t, setT] = useState<Transform>({ x: 0, y: 0, scale: 1 });
  const pointers = useRef<Map<number, { x: number; y: number }>>(new Map());
  const lastPinchDist = useRef<number | null>(null);
  const animRef = useRef<number | null>(null);

  const [renderBox, setRenderBox] = useState<Bounds>(contentBox);

  // Expand contentBox to match container's aspect ratio so scale=1 fills the canvas.
  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const update = () => {
      const cw = el.clientWidth;
      const ch = el.clientHeight;
      if (cw === 0 || ch === 0) return;
      const containerAspect = cw / ch;
      const contentAspect = contentBox.w / contentBox.h;
      let { x, y, w, h } = contentBox;
      if (containerAspect > contentAspect) {
        const newW = h * containerAspect;
        x -= (newW - w) / 2;
        w = newW;
      } else {
        const newH = w / containerAspect;
        y -= (newH - h) / 2;
        h = newH;
      }
      setRenderBox((prev) =>
        prev.x === x && prev.y === y && prev.w === w && prev.h === h ? prev : { x, y, w, h },
      );
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [contentBox]);

  // Reset zoom when the renderBox changes (window resize, content reload).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    cancelAnim();
    setT({ x: 0, y: 0, scale: 1 });
  }, [renderBox.w, renderBox.h]);

  function cancelAnim() {
    if (animRef.current != null) {
      cancelAnimationFrame(animRef.current);
      animRef.current = null;
    }
  }

  function animateTo(target: Transform) {
    cancelAnim();
    const start = { ...t };
    const t0 = performance.now();
    const step = (now: number) => {
      const k = Math.min(1, (now - t0) / ANIM_MS);
      const e = easeOutCubic(k);
      setT({
        x: start.x + (target.x - start.x) * e,
        y: start.y + (target.y - start.y) * e,
        scale: start.scale + (target.scale - start.scale) * e,
      });
      if (k < 1) animRef.current = requestAnimationFrame(step);
      else animRef.current = null;
    };
    animRef.current = requestAnimationFrame(step);
  }

  const clampScale = useCallback(
    (s: number) => Math.min(maxScale, Math.max(1, s)),
    [maxScale],
  );

  const clampTranslate = useCallback(
    (nx: number, ny: number, scale: number) => {
      if (scale <= 1.001) return { x: 0, y: 0 };
      const ox = (renderBox.w * (scale - 1)) / 2;
      const oy = (renderBox.h * (scale - 1)) / 2;
      return {
        x: Math.max(-ox, Math.min(ox, nx)),
        y: Math.max(-oy, Math.min(oy, ny)),
      };
    },
    [renderBox.w, renderBox.h],
  );

  // ─── public ────────────────────────────────────────────────────────────────
  useImperativeHandle(
    ref,
    () => ({
      zoomToBox(box, padding = 0.82) {
        // Desired scale: target box should occupy `padding` fraction of the canvas
        // along the tighter axis.
        const scaleW = (padding * renderBox.w) / box.w;
        const scaleH = (padding * renderBox.h) / box.h;
        const newScale = clampScale(Math.min(scaleW, scaleH));

        // Translate so box center lands at viewBox center.
        const tcx = box.x + box.w / 2;
        const tcy = box.y + box.h / 2;
        const vbCenterX = renderBox.x + renderBox.w / 2;
        const vbCenterY = renderBox.y + renderBox.h / 2;
        const nx = vbCenterX - tcx * newScale;
        const ny = vbCenterY - tcy * newScale;
        const clamped = clampTranslate(nx, ny, newScale);
        animateTo({ x: clamped.x, y: clamped.y, scale: newScale });
      },
      reset() {
        animateTo({ x: 0, y: 0, scale: 1 });
      },
      getScale() {
        return t.scale;
      },
    }),
    [renderBox, clampScale, clampTranslate, t.scale],
  );

  // ─── wheel + pointer ──────────────────────────────────────────────────────
  const onWheel = useCallback(
    (e: WheelEvent<SVGSVGElement>) => {
      e.preventDefault();
      cancelAnim();
      const svg = svgRef.current;
      if (!svg) return;
      const rect = svg.getBoundingClientRect();
      // wheel anchor in vbu (renderBox coords)
      const ax = renderBox.x + ((e.clientX - rect.left) / rect.width) * renderBox.w;
      const ay = renderBox.y + ((e.clientY - rect.top) / rect.height) * renderBox.h;
      setT((cur) => {
        const factor = Math.exp(-e.deltaY * 0.0018);
        const newScale = clampScale(cur.scale * factor);
        if (newScale === cur.scale) return cur;
        const realFactor = newScale / cur.scale;
        // For zoom around anchor point: keep anchor invariant on screen.
        // newTx such that ax*newScale + newTx = ax*oldScale + oldTx
        // → newTx = oldTx + ax*(oldScale - newScale)
        const nx = cur.x + ax * (cur.scale - newScale);
        const ny = cur.y + ay * (cur.scale - newScale);
        const clamped = clampTranslate(nx, ny, newScale);
        return { scale: newScale, x: clamped.x, y: clamped.y };
      });
    },
    [renderBox, clampScale, clampTranslate],
  );

  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;
    const handler = (e: globalThis.WheelEvent) => e.preventDefault();
    svg.addEventListener('wheel', handler, { passive: false });
    return () => svg.removeEventListener('wheel', handler);
  }, []);

  const onPointerDown = (e: PointerEvent<SVGSVGElement>) => {
    cancelAnim();
    (e.target as Element).setPointerCapture?.(e.pointerId);
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointers.current.size === 2) {
      lastPinchDist.current = pinchDistance(pointers.current);
    }
  };

  const onPointerMove = (e: PointerEvent<SVGSVGElement>) => {
    const prev = pointers.current.get(e.pointerId);
    if (!prev) return;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const svg = svgRef.current;
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    const pxToVbuX = renderBox.w / rect.width;
    const pxToVbuY = renderBox.h / rect.height;

    if (pointers.current.size === 1) {
      const dxVbu = (e.clientX - prev.x) * pxToVbuX;
      const dyVbu = (e.clientY - prev.y) * pxToVbuY;
      setT((cur) => {
        if (cur.scale <= 1) return cur;
        const clamped = clampTranslate(cur.x + dxVbu, cur.y + dyVbu, cur.scale);
        return { ...cur, ...clamped };
      });
    } else if (pointers.current.size === 2 && lastPinchDist.current != null) {
      const newDist = pinchDistance(pointers.current);
      const factor = newDist / lastPinchDist.current;
      lastPinchDist.current = newDist;
      const midX = midpoint(pointers.current, 'x');
      const midY = midpoint(pointers.current, 'y');
      const ax = renderBox.x + ((midX - rect.left) / rect.width) * renderBox.w;
      const ay = renderBox.y + ((midY - rect.top) / rect.height) * renderBox.h;
      setT((cur) => {
        const newScale = clampScale(cur.scale * factor);
        if (newScale === cur.scale) return cur;
        const nx = cur.x + ax * (cur.scale - newScale);
        const ny = cur.y + ay * (cur.scale - newScale);
        const clamped = clampTranslate(nx, ny, newScale);
        return { scale: newScale, x: clamped.x, y: clamped.y };
      });
    }
  };

  const onPointerUp = (e: PointerEvent<SVGSVGElement>) => {
    pointers.current.delete(e.pointerId);
    if (pointers.current.size < 2) lastPinchDist.current = null;
  };

  const zoomBy = (factor: number) => {
    cancelAnim();
    setT((cur) => {
      const newScale = clampScale(cur.scale * factor);
      if (newScale === cur.scale) return cur;
      // Zoom toward viewBox center.
      const ax = renderBox.x + renderBox.w / 2;
      const ay = renderBox.y + renderBox.h / 2;
      const nx = cur.x + ax * (cur.scale - newScale);
      const ny = cur.y + ay * (cur.scale - newScale);
      const clamped = clampTranslate(nx, ny, newScale);
      return { scale: newScale, x: clamped.x, y: clamped.y };
    });
  };

  const atMinZoom = t.scale <= 1.001;
  const atMaxZoom = t.scale >= maxScale - 0.001;

  return (
    <div
      ref={containerRef}
      className={className}
      style={{ position: 'relative', touchAction: 'none', width: '100%', height: '100%' }}
    >
      <svg
        ref={svgRef}
        viewBox={`${renderBox.x} ${renderBox.y} ${renderBox.w} ${renderBox.h}`}
        preserveAspectRatio="xMidYMid meet"
        onWheel={onWheel}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onPointerLeave={onPointerUp}
        style={{
          width: '100%',
          height: '100%',
          display: 'block',
          cursor: t.scale > 1 ? (pointers.current.size > 0 ? 'grabbing' : 'grab') : 'default',
          userSelect: 'none',
        }}
      >
        <g transform={`translate(${t.x} ${t.y}) scale(${t.scale})`}>{children(t)}</g>
      </svg>

      <div className="pointer-events-none absolute right-4 top-4 flex items-center gap-2">
        <div className="hidden items-center gap-3 rounded-md border border-border bg-bg-elevated/80 px-3 py-1.5 text-[10px] uppercase tracking-[0.18em] text-text-faint backdrop-blur md:flex">
          <span>Bölgeye tıkla · yakınlaş</span>
          <span className="text-text-faint/40">|</span>
          <span>Tekerlek · zoom</span>
        </div>
        <div className="pointer-events-auto flex items-center overflow-hidden rounded-md border border-border bg-bg-elevated/90 backdrop-blur">
          <button
            type="button"
            aria-label="Uzaklaştır"
            disabled={atMinZoom}
            onClick={() => zoomBy(1 / 1.3)}
            className="px-2.5 py-1.5 text-text-muted transition-colors hover:text-text disabled:cursor-not-allowed disabled:opacity-30"
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M5 12h14" />
            </svg>
          </button>
          <span className="mono w-12 text-center text-[10px] text-text-faint tnum">
            {Math.round(t.scale * 100)}%
          </span>
          <button
            type="button"
            aria-label="Yakınlaştır"
            disabled={atMaxZoom}
            onClick={() => zoomBy(1.3)}
            className="px-2.5 py-1.5 text-text-muted transition-colors hover:text-text disabled:cursor-not-allowed disabled:opacity-30"
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M12 5v14M5 12h14" />
            </svg>
          </button>
          <button
            type="button"
            onClick={() => animateTo({ x: 0, y: 0, scale: 1 })}
            disabled={atMinZoom && t.x === 0 && t.y === 0}
            className="border-l border-border px-3 py-1.5 text-[10px] uppercase tracking-[0.18em] text-text-muted transition-colors hover:text-text disabled:cursor-not-allowed disabled:opacity-30"
          >
            Sığdır
          </button>
        </div>
      </div>
    </div>
  );
});

function pinchDistance(ps: Map<number, { x: number; y: number }>): number {
  const it = ps.values();
  const a = it.next().value;
  const b = it.next().value;
  if (!a || !b) return 0;
  return Math.hypot(a.x - b.x, a.y - b.y);
}

function midpoint(ps: Map<number, { x: number; y: number }>, axis: 'x' | 'y'): number {
  let sum = 0;
  let n = 0;
  for (const p of ps.values()) {
    sum += p[axis];
    n++;
  }
  return n ? sum / n : 0;
}
